import type { ImageModelV2, ImageModelV2CallWarning } from '@ai-sdk/provider';
import type { Resolvable } from '@ai-sdk/provider-utils';
import {
  FetchFunction,
  combineHeaders,
  createJsonErrorResponseHandler,
  createJsonResponseHandler,
  postJsonToApi,
  resolve,
} from '@ai-sdk/provider-utils';
import { z } from 'zod';
import type { AIMLAPIImageModelId, AimlapiImageSettings } from './aimlapi-image-settings';
import { aimlapiErrorSchema } from './aimlapi-error';
type AimErr = z.infer<typeof aimlapiErrorSchema>;

interface AIMLAPIImageModelConfig {
  provider: string;
  baseURL: string;
  headers?: Resolvable<Record<string, string | undefined>>;
  fetch?: FetchFunction;
  _internal?: {
    currentDate?: () => Date;
  };
}

export class AimlapiImageModel implements ImageModelV2 {
  readonly specificationVersion = 'v2';
  readonly maxImagesPerCall = 1;

  get provider(): string {
    return this.config.provider;
  }

  constructor(
    readonly modelId: AIMLAPIImageModelId,
    readonly settings: AimlapiImageSettings = {},
    private readonly config: AIMLAPIImageModelConfig,
  ) {}

  async doGenerate({ prompt, n, aspectRatio, size, seed, providerOptions, headers, abortSignal }: Parameters<ImageModelV2['doGenerate']>[0]) {
    const warnings: ImageModelV2CallWarning[] = [];

    const modelSupportsAspectRatio = true;
    if (!modelSupportsAspectRatio && aspectRatio != null) {
      warnings.push({
        type: 'unsupported-setting',
        setting: 'aspectRatio',
        details: 'This model does not support `aspectRatio`. Use `size` instead.',
      });
    }

    if (size != null && aspectRatio != null) {
      warnings.push({
        type: 'other',
        message: 'Both `size` and `aspectRatio` were provided. The API will prioritize `size`.',
      });
    }

    const currentDate = this.config._internal?.currentDate?.() ?? new Date();
    const splitSize = size?.split('x');

    const resolvedProviderHeaders =
      (await resolve(this.config.headers)) as Record<string, string | undefined> | undefined;

    const { value: response, responseHeaders } = await postJsonToApi({
      url: `${this.config.baseURL}/images/generations`,
      headers: combineHeaders(resolvedProviderHeaders, headers),
      body: {
        model: this.modelId,
        prompt,
        seed,
        n,
        ...(splitSize && {
          width: parseInt(splitSize[0]!, 10),
          height: parseInt(splitSize[1]!, 10),
        }),
        ...(aspectRatio && { aspect_ratio: aspectRatio }),
        // Don't force base64: some models will return the url anyway
        ...(providerOptions?.aimlapi ?? {}),
      },
      successfulResponseHandler: createJsonResponseHandler(aimlapiImageResponseSchema),
      failedResponseHandler: createJsonErrorResponseHandler<AimErr>({
        errorSchema: aimlapiErrorSchema,
        errorToMessage: (e) => e.error.message,
        isRetryable: (res, e) => {
          if (res.status === 429) return true;
          if (res.status >= 500) return true;
          if (e?.error?.code === 'overloaded' || e?.error?.code === 'timeout') return true;
          return false;
        },
      }),
      abortSignal,
      fetch: this.config.fetch,
    });

    // response.normalized: string[] (b64 or url)
    return {
      images: response.normalized,
      warnings,
      response: {
        timestamp: currentDate,
        modelId: this.modelId,
        headers: responseHeaders,
      },
    };
  }
}

// We accept both possible formats and normalize
const aimlapiImageResponseSchema = z.union([
  // Option A: OpenAI-compatible base64
  z.object({
    data: z.array(z.object({ b64_json: z.string() })),
  }).transform((v) => ({ normalized: v.data.map((d) => d.b64_json) })),

  // Option B: AIMLAPI format with images (url or b64_json)
  z.object({
    images: z.array(
      z.object({
        b64_json: z.string().optional(),
        url: z.string().url().optional(),
      }),
    ),
  }).transform((v) => ({
    normalized: v.images
      .map((i) => i.b64_json ?? i.url ?? '')
      .filter((x) => x.length > 0),
  })),
]);
